import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import config from '../../config';

export default function ChangePackage() {
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ QUERY PARAM FIX
  const params = new URLSearchParams(location.search);
  const packageId = params.get('packageId');
  const bookingId = params.get('bookingId');

  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(packageId || '');
  const [selectedPrices, setSelectedPrices] = useState([]);
  const [selectedPriceId, setSelectedPriceId] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const token = sessionStorage.getItem('token');

  // ✅ FIXED FETCH
  const fetchPackages = async () => {
    try {
      if (!packageId) return;

      setLoading(true);

      const res = await fetch(
        `${config.API_BASE_URL}/home/GetUpdatePackages/${packageId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!res.ok) throw new Error(await res.text());

      let data = [];

      try {
        data = await res.json();
        console.log('Package Data:', data);
      } catch {
        console.log('⚠️ Empty response');
      }

      // ✅ FINAL FIX (ARRAY HANDLE)
      setPackages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: err.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // ✅ LOAD DATA
  useEffect(() => {
    fetchPackages();
  }, [packageId]);

  // ✅ UPDATE PACKAGE
  const handleUpdatePackage = async () => {
    if (loading) return; // guard against a second click while in-flight
    if (!selectedPackage || !selectedPriceId) {
      setSnackbar({
        open: true,
        message: 'Please select package & price',
        severity: 'warning',
      });
      return;
    }

    try {
      setLoading(true);

      const payload = {
        BookingId: bookingId,
        PackageId: selectedPackage,
        PriceId: selectedPriceId,
      };

      const res = await fetch(`${config.API_BASE_URL}/Home/ChangePackage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(await res.text());

      // Backend now returns { status } — only treat 'success' as success so a
      // failed package change no longer shows a false "updated" message.
      const body = await res.json().catch(() => ({}));
      if (body && body.status && body.status !== 'success') {
        throw new Error(body.message || 'Package change failed.');
      }

      setSnackbar({
        open: true,
        message: 'Package updated successfully!',
        severity: 'success',
      });

      setTimeout(() => {
        navigate('/admin/BookingReport');
      }, 1500);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant='h5' mb={3} textAlign='center'>
        Change Package
      </Typography>

      {loading && <CircularProgress />}

      <Box display='flex' flexWrap='wrap' gap={3} justifyContent='center'>
        {packages.length === 0 ? (
          <Typography>No packages found</Typography>
        ) : (
          packages.map((pkg) => (
            <Paper key={pkg.packageId} sx={{ p: 3, width: 260 }}>
              <Typography variant='h6'>{pkg.packageName}</Typography>

              <Typography mt={2} fontWeight='bold'>
                ₹ {pkg.pricesList?.[0]?.price || 0}
              </Typography>

              <Button
                fullWidth
                variant='contained'
                sx={{ mt: 2 }}
                onClick={() => {
                  setSelectedPackage(pkg.packageId);
                  setSelectedPrices(pkg.pricesList || []);
                  setSelectedPriceId(null);
                  setOpenModal(true);
                }}>
                View Pricing Options
              </Button>
            </Paper>
          ))
        )}
      </Box>

      <Dialog open={openModal} onClose={() => setOpenModal(false)}>
        <DialogTitle>Select Package Price</DialogTitle>
        <DialogContent>
          {selectedPrices.map((p) => (
            <Button
              key={p.priceId}
              fullWidth
              sx={{ mb: 2 }}
              variant={selectedPriceId === p.priceId ? 'contained' : 'outlined'}
              onClick={() => setSelectedPriceId(p.priceId)}>
              ₹ {p.price}
            </Button>
          ))}

          <Button
            fullWidth
            variant='contained'
            color='success'
            disabled={!selectedPriceId}
            onClick={() => setOpenModal(false)}>
            Confirm Price
          </Button>
        </DialogContent>
      </Dialog>

      <Box mt={4} textAlign='center'>
        <Button
          variant='contained'
          onClick={handleUpdatePackage}
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={16} color='inherit' /> : null
          }>
          {loading ? 'Updating...' : 'Update Package'}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
